import type { RequestListEntryV2 } from 'rest/request-list';

import type {
	FetchRequestListSuccess,
	MarkInitialPageLoadRendered,
	InvalidatePages,
} from 'state/actions/request-list';
import {
	FETCH_REQUEST_LIST_SUCCESS,
	MARK_INITIAL_PAGE_LOAD_RENDERED,
	INVALIDATE_PAGES,
} from 'state/actions/request-list';
import type { SortOrderType } from '@atlaskit/dynamic-table/types';
import { initialModel } from '@atlassian/help-center-common-util/model';
import {
	isEmptyStringFilterOnFirstReqListLoadEnabled,
	isRequestListInfiniteScrollEnabled,
} from '@helpcenter/feature-flags';

export interface PagesV2 {
	isInitialPageLoad: boolean;
	[page: number]: RequestListEntryV2[];
}
export interface RequestListState {
	resultsPerPage: number;
	totalResults: number;
	sortBy?: string;
	sortOrder?: SortOrderType;
	pages: PagesV2;
	nextToken?: string;
	allRows?: RequestListEntryV2[];
	filter?: string;
}

const emptyState: RequestListState = {
	totalResults: 0,
	resultsPerPage: 20,
	pages: { isInitialPageLoad: false },
};

export const getInitialRequestListState = (): RequestListState => {
	const initialModelState = initialModel();
	if (initialModelState && initialModelState.allReqFilter) {
		const { resultsPerPage, totalResults, sortBy, sortOrder, requestList, nextToken } =
			initialModelState.allReqFilter;
		const baseState: RequestListState = {
			resultsPerPage,
			totalResults,
			sortBy,
			sortOrder,
			pages: {
				isInitialPageLoad: true,
				[initialModelState.allReqFilter.selectedPage]: requestList,
			},
		};

		if (isRequestListInfiniteScrollEnabled()) {
			baseState.nextToken = nextToken;
			baseState.allRows = requestList ?? [];
			baseState.filter = isEmptyStringFilterOnFirstReqListLoadEnabled()
				? (initialModelState.allReqFilter.filter ?? '')
				: initialModelState.allReqFilter.filter;
		}

		return baseState;
	}
	return emptyState;
};

export type HandledActions =
	| FetchRequestListSuccess
	| MarkInitialPageLoadRendered
	| InvalidatePages;

const defaultState: RequestListState = getInitialRequestListState();

export function requestListReducer(
	state: RequestListState = defaultState,
	action: HandledActions,
): RequestListState {
	switch (action.type) {
		case FETCH_REQUEST_LIST_SUCCESS:
			return handleFetchRequestListSuccess(state, action);
		case MARK_INITIAL_PAGE_LOAD_RENDERED:
			return handleMarkInitialPageLoadRendered(state);
		case INVALIDATE_PAGES:
			return handleInvalidatePages(state, action);
		default:
			return state;
	}
}

function handleInvalidatePages(state: RequestListState, action: InvalidatePages): RequestListState {
	const currentPage = action.payload;

	return {
		...state,
		pages: {
			isInitialPageLoad: false,
			[currentPage]: state.pages[currentPage],
		},
	};
}

function handleFetchRequestListSuccessInfiniteScroll(
	state: RequestListState,
	action: FetchRequestListSuccess,
): RequestListState {
	const response = action.payload;
	if (!response) {
		return state;
	}

	const isReset =
		response.sortBy !== state.sortBy ||
		response.sortOrder !== state.sortOrder ||
		response.totalResults !== state.totalResults ||
		response.filter !== state.filter;

	const existingRows = state.allRows ?? [];
	// Deduplicate to prevent duplicate entries from race conditions or overlapping API calls
	const existingKeys = new Set(existingRows.map((row) => row.key));
	const uniqueNewRows = response.requestList.filter((row) => !existingKeys.has(row.key));
	const newRows = isReset ? response.requestList : [...existingRows, ...uniqueNewRows];

	return {
		...state,
		resultsPerPage: response.resultsPerPage,
		totalResults: response.totalResults,
		sortBy: response.sortBy,
		sortOrder: response.sortOrder,
		nextToken: response.nextToken,
		filter: response.filter,
		allRows: newRows,
		// The `pages` object (keyed by selectedPage) is only used by the non-infinite-scroll
		// pagination path. In the infinite scroll path, `allRows` is the canonical source of
		// truth for all fetched rows, so we do not populate `pages[selectedPage]` here.
		// Doing so would store stale page-indexed data that is never consumed by infinite
		// scroll consumers, and `selectedPage` is irrelevant in nextToken-based pagination.
		pages: {
			isInitialPageLoad: false,
		},
	};
}

function handleFetchRequestListSuccess(
	state: RequestListState,
	action: FetchRequestListSuccess,
): RequestListState {
	if (isRequestListInfiniteScrollEnabled()) {
		return handleFetchRequestListSuccessInfiniteScroll(state, action);
	}

	const response = action.payload;
	if (response) {
		if (response.totalResults === state.totalResults) {
			if (response.sortBy !== state.sortBy || response.sortOrder !== state.sortOrder) {
				if (response.selectedPage === 1) {
					return {
						...state,
						sortOrder: response.sortOrder,
						sortBy: response.sortBy,
						pages: {
							isInitialPageLoad: false,
							[response.selectedPage]: response.requestList,
						},
					};
				}
			}
			return {
				...state,
				pages: {
					...state.pages,
					isInitialPageLoad: false,
					[response.selectedPage]: response.requestList,
				},
			};
		}
		return {
			resultsPerPage: response.resultsPerPage,
			totalResults: response.totalResults,
			sortBy: response.sortBy,
			sortOrder: response.sortOrder,
			pages: {
				isInitialPageLoad: false,
				[response.selectedPage]: response.requestList,
			},
		};
	}
	return state;
}

const handleMarkInitialPageLoadRendered = (state: RequestListState): RequestListState => ({
	...state,
	pages: {
		...state.pages,
		isInitialPageLoad: false,
	},
});
